/**
 * Archivo: src/pages/core-auth/SignInPage.jsx
 * Descripción: Login standalone de Sinapso Core conservando el layout visual
 *              del proyecto original y la autenticación del sitio nuevo.
 */

import React, { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Icon } from "@iconify/react";
import { toast, ToastContainer } from "react-toastify";
import { setSessionHint } from "../../platform/sessionHint";

const BRAND_NAME = process.env.REACT_APP_TENANT_COMPANY_NAME || "Sinapso Core Platform";
const LOGO = "/ui-static/images/logo.png";
const LOGO_FALLBACK = "/assets-neutral/logo.png";
const AUTH_IMAGE = "/ui-static/images/auth/auth-img.png";

function resolveTenantDomain(edgeContext) {
  if (edgeContext?.tenant_domain) {
    return edgeContext.tenant_domain;
  }

  if (typeof window !== "undefined" && window.location?.hostname) {
    return window.location.hostname;
  }

  return "app.sinapso.cl";
}

export default function SignInPage({ edgeContext, onLoginSuccess }) {
  const navigate = useNavigate();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [mfaChallenge, setMfaChallenge] = useState("");
  const [mfaCode, setMfaCode] = useState("");
  const [logoSrc, setLogoSrc] = useState(LOGO);

  const tenantDomain = useMemo(
    () => resolveTenantDomain(edgeContext),
    [edgeContext]
  );

  const submitLogin = async (event) => {
    event.preventDefault();

    const cleanEmail = email.trim().toLowerCase();

    if (!mfaChallenge && (!cleanEmail || !password)) {
      toast.error("Por favor, ingresa tu email y contraseña.");
      return;
    }
    if (mfaChallenge && !/^\d{6}$/.test(mfaCode)) {
      toast.error("Ingresa el código MFA de 6 dígitos.");
      return;
    }

    setIsSubmitting(true);
    let loginTraceId = "";

    try {
      console.info("[Login Trace] Enviando solicitud de acceso", { tenantDomain, flow: mfaChallenge ? "mfa" : "password" });
      const response = await fetch(mfaChallenge ? "/api/auth/mfa/verify" : "/api/auth/login", {
        method: "POST",
        credentials: "same-origin",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "X-Session-Transport": "cookie",
        },
        body: JSON.stringify(mfaChallenge ? { challenge_token: mfaChallenge, code: mfaCode } : { email: cleanEmail, password, tenantDomain }),
      });
      loginTraceId = response.headers.get("X-Login-Trace-Id") || "";

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        console.warn("[Login Trace] El servidor rechazó el acceso", { status: response.status, traceId: loginTraceId || "(sin id)", error: data.error || "", message: data.message || "" });
        throw new Error(
          data.message || data.error || "Credenciales inválidas."
        );
      }

      console.info("[Login Trace] Respuesta de acceso exitosa", { status: response.status, traceId: loginTraceId || "(sin id)", mfaRequired: Boolean(data.mfa_required) });

      if (data.mfa_required) {
        setMfaChallenge(data.challenge_token);
        toast.info("Ingresa el código de tu aplicación autenticadora.");
        return;
      }

      if (!data.cookie_session && !data.token) {
        throw new Error("El servidor no devolvió un token de sesión.");
      }

      if (data.token) sessionStorage.setItem("syrakis_token", data.token);
      else sessionStorage.removeItem("syrakis_token");
      setSessionHint(data.user, data.expiresIn ?? data.expires_in ?? 28800);

      if (typeof onLoginSuccess === "function") {
        onLoginSuccess(data);
      }

      toast.success("Sesión iniciada correctamente.");

      window.setTimeout(() => {
        navigate("/dashboard", { replace: true });
      }, 250);
    } catch (error) {
      console.error("[Login Trace] Falló el inicio de sesión", { traceId: loginTraceId || "(sin id; revisar Network/servidor)", message: error?.message || String(error) });

      const message =
        error?.message === "rate_limited"
          ? "Demasiados intentos. Espera unos segundos."
          : error?.message || "Credenciales inválidas.";

      toast.error(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="auth bg-base d-flex flex-wrap">
      <ToastContainer
        position="top-right"
        autoClose={3200}
        hideProgressBar={false}
      />

      {/* IMPORTANTE: se conserva exactamente la estructura de layout
          que espera style.css del proyecto original. */}
      <div className="auth-left d-lg-block d-none">
        <div className="d-flex align-items-center flex-column h-100 justify-content-center px-32">
          <img
            src={AUTH_IMAGE}
            alt="Acceso seguro a Sinapso Core Platform"
            onError={(event) => {
              event.currentTarget.style.display = "none";
            }}
          />
        </div>
      </div>

      <div className="auth-right py-32 px-24 d-flex flex-column justify-content-center">
        <div className="max-w-464-px mx-auto w-100">
          <div>
            <Link
              to="/login"
              className="mb-40 max-w-290-px d-inline-block"
            >
              <img
                src={logoSrc}
                alt={BRAND_NAME}
                className="max-w-290-px"
                onError={() => {
                  if (logoSrc !== LOGO_FALLBACK) {
                    setLogoSrc(LOGO_FALLBACK);
                  }
                }}
              />
            </Link>

            <h4 className="mb-12">{mfaChallenge ? "Verifica tu identidad" : `Ingresa a ${BRAND_NAME}`}</h4>

            {mfaChallenge && <p className="mb-32 text-secondary-light text-lg">Escribe el código de 6 dígitos de tu aplicación autenticadora.</p>}
          </div>

          <form onSubmit={submitLogin} noValidate>
            {mfaChallenge && <div className="icon-field mb-16"><span className="icon top-50 translate-middle-y"><Icon icon="solar:shield-keyhole-outline" /></span><input inputMode="numeric" pattern="[0-9]{6}" maxLength={6} autoComplete="one-time-code" className="form-control h-56-px bg-neutral-50 radius-12" placeholder="Código MFA" value={mfaCode} onChange={(event) => setMfaCode(event.target.value.replace(/\D/g, ""))} disabled={isSubmitting} required /></div>}
            {!mfaChallenge && <div className="icon-field mb-16">
              <span className="icon top-50 translate-middle-y">
                <Icon icon="mage:email" />
              </span>

              <input
                type="email"
                autoComplete="username"
                className="form-control h-56-px bg-neutral-50 radius-12"
                placeholder="Email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                disabled={isSubmitting}
                required
              />
            </div>}

            {!mfaChallenge && <div className="position-relative mb-20">
              <div className="icon-field">
                <span className="icon top-50 translate-middle-y">
                  <Icon icon="solar:lock-password-outline" />
                </span>

                <input
                  type={showPassword ? "text" : "password"}
                  autoComplete="current-password"
                  className="form-control h-56-px bg-neutral-50 radius-12"
                  placeholder="Contraseña"
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  disabled={isSubmitting}
                  required
                />
              </div>

              <button
                type="button"
                className="position-absolute end-0 top-50 translate-middle-y me-16 text-secondary-light border-0 bg-transparent"
                onClick={() => setShowPassword((value) => !value)}
                aria-label={
                  showPassword
                    ? "Ocultar contraseña"
                    : "Mostrar contraseña"
                }
              >
                <Icon
                  icon={
                    showPassword
                      ? "solar:eye-closed-linear"
                      : "solar:eye-linear"
                  }
                />
              </button>
            </div>}

            <button
              type="submit"
              className="btn btn-primary text-sm btn-sm px-12 py-16 w-100 radius-12 mt-32"
              disabled={isSubmitting}
            >
              {isSubmitting ? "Validando..." : mfaChallenge ? "Verificar MFA" : "Ingresar a la Plataforma"}
            </button>

          </form>
        </div>
      </div>
    </section>
  );
}
